import  { createContext, useContext, useReducer, ReactNode, useEffect } from 'react';
import { AppState, Invoice, Client, Analytics } from '../types';
import { useAuth } from './AuthContext';
import {
  createClient as createClientFirestore,
  getWorkspaceClients,
  updateClient as updateClientFirestore,
  deleteClient as deleteClientFirestore,
  createInvoice as createInvoiceFirestore,
  getWorkspaceInvoices,
  updateInvoice as updateInvoiceFirestore,
  deleteInvoice as deleteInvoiceFirestore,
  subscribeToWorkspaceClients,
  subscribeToWorkspaceInvoices,
} from '../services/firestore';

interface AppContextType extends AppState {
  addClient: (client: Omit<Client, 'id' | 'workspaceId' | 'createdAt'>) => Promise<void>;
  updateClient: (id: string, client: Partial<Client>) => Promise<void>;
  deleteClient: (id: string) => Promise<void>;
  createInvoice: (invoice: Omit<Invoice, 'id' | 'workspaceId' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateInvoice: (id: string, invoice: Partial<Invoice>) => Promise<void>;
  deleteInvoice: (id: string) => Promise<void>;
  generatePaymentLink: (invoiceId: string) => string;
  markInvoiceAsPaid: (invoiceId: string) => Promise<void>;
  sendInvoice: (invoiceId: string) => Promise<void>;
  refreshAnalytics: () => void;
  getNextInvoiceNumber: () => string;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

type Action =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_CLIENTS'; payload: Client[] }
  | { type: 'ADD_CLIENT'; payload: Client }
  | { type: 'UPDATE_CLIENT'; payload: { id: string; client: Partial<Client> } }
  | { type: 'DELETE_CLIENT'; payload: string }
  | { type: 'SET_INVOICES'; payload: Invoice[] }
  | { type: 'CREATE_INVOICE'; payload: Invoice }
  | { type: 'UPDATE_INVOICE'; payload: { id: string; invoice: Partial<Invoice> } }
  | { type: 'DELETE_INVOICE'; payload: string }
  | { type: 'SET_ANALYTICS'; payload: Analytics };

const initialState: AppState = {
  user: null,
  currentWorkspace: null,
  workspaces: [],
  clients: [],
  invoices: [],
  analytics: null,
  isAuthenticated: false,
  isLoading: true,
};

function appReducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_CLIENTS':
      return { ...state, clients: action.payload };
    case 'ADD_CLIENT':
      return { ...state, clients: [...state.clients, action.payload] };
    case 'UPDATE_CLIENT':
      return {
        ...state,
        clients: state.clients.map(client =>
          client.id === action.payload.id ? { ...client, ...action.payload.client } : client
        )
      };
    case 'DELETE_CLIENT':
      return {
        ...state,
        clients: state.clients.filter(client => client.id !== action.payload)
      };
    case 'SET_INVOICES':
      return { ...state, invoices: action.payload };
    case 'CREATE_INVOICE':
      return { ...state, invoices: [...state.invoices, action.payload] };
    case 'UPDATE_INVOICE':
      return {
        ...state,
        invoices: state.invoices.map(invoice =>
          invoice.id === action.payload.id ? { ...invoice, ...action.payload.invoice } : invoice
        )
      };
    case 'DELETE_INVOICE':
      return {
        ...state,
        invoices: state.invoices.filter(invoice => invoice.id !== action.payload)
      };
    case 'SET_ANALYTICS':
      return { ...state, analytics: action.payload };
    default:
      return state;
  }
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const { currentWorkspace, isAuthenticated, user } = useAuth();

  useEffect(() => {
    if (isAuthenticated && currentWorkspace) {
      loadData();
    } else if (!isAuthenticated) {
      // Clear all app state when user is not authenticated
      dispatch({ type: 'SET_CLIENTS', payload: [] });
      dispatch({ type: 'SET_INVOICES', payload: [] });
      // Reset analytics to initial state
      dispatch({ type: 'SET_ANALYTICS', payload: {
        totalRevenue: 0,
        monthlyRevenue: 0,
        totalInvoices: 0,
        paidInvoices: 0,
        pendingInvoices: 0,
        overdueInvoices: 0,
        averageInvoiceValue: 0,
        revenueGrowth: 0,
        clientGrowth: 0,
        monthlyData: [],
        topClients: []
      }});
    }
  }, [isAuthenticated, currentWorkspace]);

  useEffect(() => {
    if (!currentWorkspace) return;

    // Set up real-time listeners for clients and invoices
    const unsubscribeClients = subscribeToWorkspaceClients(
      currentWorkspace.id,
      (clients) => {
        dispatch({ type: 'SET_CLIENTS', payload: clients });
      }
    );

    const unsubscribeInvoices = subscribeToWorkspaceInvoices(
      currentWorkspace.id,
      (invoices) => {
        dispatch({ type: 'SET_INVOICES', payload: invoices });
        // Recalculate analytics when invoices change
        calculateAnalytics(invoices);
      }
    );

    return () => {
      unsubscribeClients();
      unsubscribeInvoices();
    };
  }, [currentWorkspace]);

  const loadData = async () => {
    if (!currentWorkspace) return;
    
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      // Load clients and invoices from Firestore
      const [clients, invoices] = await Promise.all([
        getWorkspaceClients(currentWorkspace.id),
        getWorkspaceInvoices(currentWorkspace.id),
      ]);

      dispatch({ type: 'SET_CLIENTS', payload: clients });
      dispatch({ type: 'SET_INVOICES', payload: invoices });
      
      // Calculate analytics
      calculateAnalytics(invoices);
      
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const calculateAnalytics = (invoices: Invoice[]) => {
    const totalRevenue = invoices
      .filter(inv => inv.status === 'paid')
      .reduce((sum, inv) => sum + inv.total, 0);

    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    const monthlyRevenue = invoices
      .filter(inv => {
        const date = new Date(inv.createdAt);
        return inv.status === 'paid' && 
               date.getMonth() === currentMonth && 
               date.getFullYear() === currentYear;
      })
      .reduce((sum, inv) => sum + inv.total, 0);

    const totalInvoices = invoices.length;
    const paidInvoices = invoices.filter(inv => inv.status === 'paid').length;
    const pendingInvoices = invoices.filter(inv => inv.status === 'sent' || inv.status === 'viewed').length;
    const overdueInvoices = invoices.filter(inv => {
      const dueDate = new Date(inv.dueDate);
      return inv.status !== 'paid' && dueDate < new Date();
    }).length;

    const averageInvoiceValue = totalInvoices > 0 ? totalRevenue / paidInvoices : 0;

    // Generate monthly data for the last 12 months
    const monthlyData = [];
    for (let i = 11; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const month = date.toLocaleString('default', { month: 'short' });
      
      const monthInvoices = invoices.filter(inv => {
        const invDate = new Date(inv.createdAt);
        return invDate.getMonth() === date.getMonth() && 
               invDate.getFullYear() === date.getFullYear();
      });

      const revenue = monthInvoices
        .filter(inv => inv.status === 'paid')
        .reduce((sum, inv) => sum + inv.total, 0);

      monthlyData.push({
        month,
        revenue,
        invoices: monthInvoices.length,
        clients: new Set(monthInvoices.map(inv => inv.clientId)).size,
      });
    }

    const analytics: Analytics = {
      totalRevenue,
      monthlyRevenue,
      totalInvoices,
      paidInvoices,
      pendingInvoices,
      overdueInvoices,
      averageInvoiceValue,
      revenueGrowth: 0, // Could calculate this based on previous months
      clientGrowth: 0,  // Could calculate this based on client creation dates
      monthlyData,
      topClients: [], // Could calculate this from invoice data
    };

    dispatch({ type: 'SET_ANALYTICS', payload: analytics });
  };

  const getNextInvoiceNumber = (): string => {
    const prefix = currentWorkspace?.settings.invoicePrefix || 'INV';
    const existingNumbers = state.invoices
      .map(inv => inv.invoiceNumber)
      .filter(num => num.startsWith(prefix))
      .map(num => parseInt(num.replace(prefix, '')) || 0)
      .sort((a, b) => b - a);
    
    const nextNumber = existingNumbers.length > 0 ? existingNumbers[0] + 1 : 1;
    return `${prefix}${nextNumber.toString().padStart(6, '0')}`;
  };

  const addClient = async (clientData: Omit<Client, 'id' | 'workspaceId' | 'createdAt'>) => {
    if (!currentWorkspace) return;

    try {
      const client = await createClientFirestore({
        ...clientData,
        workspaceId: currentWorkspace.id,
      });
      dispatch({ type: 'ADD_CLIENT', payload: client as unknown as Client });
    } catch (error) {
      console.error('Error adding client:', error);
      throw error;
    }
  };

  const updateClient = async (id: string, clientData: Partial<Client>) => {
    try {
      await updateClientFirestore(id, clientData);
      dispatch({ type: 'UPDATE_CLIENT', payload: { id, client: clientData } });
    } catch (error) {
      console.error('Error updating client:', error);
      throw error;
    }
  };

  const deleteClient = async (id: string) => {
    try {
      await deleteClientFirestore(id);
      dispatch({ type: 'DELETE_CLIENT', payload: id });
    } catch (error) {
      console.error('Error deleting client:', error);
      throw error;
    }
  };

  const createInvoice = async (invoiceData: Omit<Invoice, 'id' | 'workspaceId' | 'createdAt' | 'updatedAt'>) => {
    if (!currentWorkspace) return;

    try {
      const invoice = await createInvoiceFirestore({
        ...invoiceData,
        workspaceId: currentWorkspace.id,
      });
      dispatch({ type: 'CREATE_INVOICE', payload: invoice as unknown as Invoice });
    } catch (error) {
      console.error('Error creating invoice:', error);
      throw error;
    }
  };

  const updateInvoice = async (id: string, invoiceData: Partial<Invoice>) => {
    try {
      await updateInvoiceFirestore(id, invoiceData);
      dispatch({ type: 'UPDATE_INVOICE', payload: { id, invoice: invoiceData } });
    } catch (error) {
      console.error('Error updating invoice:', error);
      throw error;
    }
  };

  const deleteInvoice = async (id: string) => {
    try {
      await deleteInvoiceFirestore(id);
      dispatch({ type: 'DELETE_INVOICE', payload: id });
    } catch (error) {
      console.error('Error deleting invoice:', error);
      throw error;
    }
  };

  const generatePaymentLink = (invoiceId: string): string => {
    return `${window.location.origin}/pay/${invoiceId}`;
  };

  const markInvoiceAsPaid = async (invoiceId: string) => {
    try {
      await updateInvoiceFirestore(invoiceId, {
        status: 'paid',
        paymentStatus: 'completed',
        paidAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      throw error;
    }
  };

  const sendInvoice = async (invoiceId: string) => {
    try {
      await updateInvoiceFirestore(invoiceId, {
        status: 'sent',
        sentAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error sending invoice:', error);
      throw error;
    }
  };

  const refreshAnalytics = () => {
    calculateAnalytics(state.invoices);
  };

  const contextValue: AppContextType = {
    ...state,
    user,
    currentWorkspace,
    workspaces: [],
    isAuthenticated,
    addClient,
    updateClient,
    deleteClient,
    createInvoice,
    updateInvoice,
    deleteInvoice,
    generatePaymentLink,
    markInvoiceAsPaid,
    sendInvoice,
    refreshAnalytics,
    getNextInvoiceNumber,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}